import { AnalysisStageSchema, type AssetKind } from "@scenix/core";
import type { AnalysisEngine, AnalysisStage } from "./api";

/**
 * Which analysis stages could produce anything for a kind of asset.
 *
 * The mirror of `domain.StagesForKind`, and the same list in the same order.
 * Go is authoritative; this exists so a surface offering "run this stage again"
 * can leave out the ones the API would refuse, rather than presenting a control
 * whose only outcome is a 400 (ADR 000080).
 *
 * Derived rather than served for the reason `uploadAbandoned` is: a field on
 * the response would be a second copy that can disagree with the first, and the
 * way anybody finds out is the wrong control on screen. The gate is four lines
 * of "does this thing have a picture track", not a policy that moves.
 */
export function stagesForKind(kind: AssetKind): AnalysisStage[] {
  return STAGE_ORDER.filter((stage) => appliesTo[stage](kind));
}

/**
 * Every stage, in pipeline order.
 *
 * Read rather than restated: the schema declares its values are in pipeline
 * order, which is the order `domain.AnalysisStages()` has them in (ADR 000063).
 * A copy of the list here was annotated `AnalysisStage[]`, so it caught a stage
 * being removed and never one being added: a fifth stage would be missing from
 * what the asset page says was filled and from what re-analysis offers to
 * overwrite, which is the surface ADR 000073 built to report a gap, and nothing
 * would have objected (issue #358).
 */
const STAGE_ORDER: AnalysisStage[] = [...AnalysisStageSchema.options];

/** A video and a recording are the two kinds that run for a length of time. */
function timed(kind: AssetKind): boolean {
  return kind === "video" || kind === "audio";
}

/** A video and an image are the two kinds ffmpeg can sample a picture from. */
function pictured(kind: AssetKind): boolean {
  return kind === "video" || kind === "image";
}

const appliesTo: Record<AnalysisStage, (kind: AssetKind) => boolean> = {
  probe: (kind) => timed(kind) || pictured(kind),
  thumbnail: pictured,
  transcribe: timed,
  // Every kind. A document and a link get a summary and topics from their text,
  // which is the whole of their analysis.
  describe: () => true,
};

/**
 * What a stage produces, named as the thing rather than the step that makes it,
 * so one phrase serves both "this is missing" and "make this again".
 *
 * Keyed by kind as well as stage, because two of the four produce a different
 * thing depending on what they are pointed at, and naming the video's half of
 * it for everything reads as being about some other asset. Somebody told their
 * PDF is missing a description of what is on screen concludes the row is a bug
 * and leaves the gap alone, which is the gap that keeps that document out of
 * search: ADR 000045 indexes the summary and the topics `describe` fills.
 */
export function stageProduces(stage: AnalysisStage, kind: AssetKind): string {
  return produces[stage](kind);
}

const produces: Record<AnalysisStage, (kind: AssetKind) => string> = {
  // An image has a container too, but nobody thinks of a PNG that way: the
  // fields the stage fills for one are its dimensions and its size on disk.
  probe: (kind) => (kind === "image" ? "the dimensions and file details" : "the container details"),
  // A video's filmstrip is several moments through it; an image has one frame,
  // so "stills" would promise a row that is never coming (ADR 000082).
  thumbnail: (kind) => (kind === "image" ? "a cover still" : "stills"),
  transcribe: () => "a transcript",
  // The walk through the picture arrives for a video alone. The describer is
  // told to leave visuals empty where there is no moving picture, and
  // `MissingStages` agrees: for every other kind the summary and the topics are
  // the whole of what the stage produces.
  describe: (kind) =>
    kind === "video" ? "a description of what is on screen" : "a summary and what it is about",
};

/**
 * Whose work an overwrite would destroy, in the words the API's own refusal
 * uses (`describeEngine`), so the warning before the click and the message
 * after one read the same.
 *
 * A manifest attached by set_analysis need not say what produced it, and an
 * absent engine is absent whether it arrives as nothing or as an empty string.
 */
export function engineLabel(engine: string | undefined): string {
  return engine ? `"${engine}"` : "the existing analysis";
}

/**
 * An engine in the words its vendor uses, for telling somebody which key a
 * deployment is missing (ADR 000129).
 *
 * A label rather than a rule: which engine a stage needs is decided in Go and
 * served with the stage (`unfillableStages`), so this never has to agree with
 * anything except the vendor's own spelling. `Record` rather than a function
 * with a default, so a third engine added to the schema fails the build here
 * instead of drawing its own identifier at somebody.
 */
export function engineName(engine: AnalysisEngine): string {
  return engineNames[engine];
}

const engineNames: Record<AnalysisEngine, string> = {
  elevenlabs: "ElevenLabs",
  gemini: "Gemini",
};

/**
 * A list the way a sentence writes one, under the conjunction the sentence
 * needs: things that are all absent read "or", things that all happened read
 * "and".
 *
 * Here rather than in the route because the sentence it serves is about what a
 * deployment can run, and duplicates are meaningless in it: two stages needing
 * one engine name that engine once.
 */
export function listOf(items: string[], conjunction: "and" | "or" = "and"): string {
  const unique = [...new Set(items)];
  if (unique.length <= 1) return unique[0] ?? "";
  return `${unique.slice(0, -1).join(", ")} ${conjunction} ${unique[unique.length - 1]}`;
}
